import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  Button,
  Text,
  Stack,
  Group,
  Badge,
  Paper,
  Checkbox,
  Textarea,
  SimpleGrid,
  Select,
  TextInput,
  NumberInput,
  ScrollArea,
  Divider,
} from "@mantine/core";
import { IconUserCheck } from "@tabler/icons-react";
import {
  IconCheck,
  IconX,
  IconBeach,
  IconCalendarOff,
  IconTool,
  IconSchool,
  IconPlayerPause,
  IconPlayerPlay,
  IconCalendarMinus,
} from "@tabler/icons-react";

interface GuestStudent {
  name: string;
  email: string;
  studentClasses: string;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  onClassHappened: () => void;
  onQuickSave: (
    status: "Leave" | "Holiday" | "Not Started" | "Not Schedule",
    issueData?: { is_issue: boolean; issue_description: string },
  ) => void;
  onGuestLecture?: (
    guestEmail: string,
    guestName: string,
    numSessions: number,
    students: GuestStudent[],
  ) => void;
  onHoldBatch?: (fromDate: string, toDate: string, comment: string) => void;
  onUnholdBatch?: () => void;
  isSubmitting: boolean;
  isFirstJourney?: boolean;
  instructorsDropdown?: { label: string; value: string; name: string }[];
  currentBatchStatus?: string;
  // ── Guest lecture data props ──
  currentStudents?: GuestStudent[];
  currentNumSessions?: number;
  // ── Hold editing props ──
  isHoldDate?: boolean;
  onEndHoldFromDate?: (fromDate: string) => void;
  holdFromDate?: string;
  holdToDate?: string;
}

const formatDate = (d: Date | null) =>
  d
    ? d.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

// Convert Date → "YYYY-MM-DD" for <input type="date">
const toInputValue = (d: Date | null): string => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const today = toInputValue(new Date());

const ClassStatusDialog = ({
  opened,
  onClose,
  selectedDate,
  onClassHappened,
  onQuickSave,
  onGuestLecture,
  onHoldBatch,
  onUnholdBatch,
  isSubmitting,
  isFirstJourney = false,
  instructorsDropdown = [],
  currentBatchStatus,
  currentStudents = [],
  currentNumSessions = 0,
  isHoldDate = false,
  onEndHoldFromDate,
  holdFromDate,
  holdToDate,
}: Props) => {
  const [showNoOptions, setShowNoOptions] = useState(false);

  // ── Leave issue marking state ──
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaveIsIssue, setLeaveIsIssue] = useState(false);
  const [leaveIssueDescription, setLeaveIssueDescription] = useState("");

  // ── Guest Lecture state ──
  const [showGuestLectureForm, setShowGuestLectureForm] = useState(false);
  const [selectedGuestEmail, setSelectedGuestEmail] = useState<string | null>(
    null,
  );
  const [guestNumSessions, setGuestNumSessions] = useState<number>(0);
  const [guestStudents, setGuestStudents] = useState<GuestStudent[]>([]);

  // ── Hold state (native string values "YYYY-MM-DD") ──
  const [showHoldForm, setShowHoldForm] = useState(false);
  const [holdFromStr, setHoldFromStr] = useState<string>("");
  const [holdToStr, setHoldToStr] = useState<string>("");
  const [holdComment, setHoldComment] = useState("");

  // Derive guest name from selected email
  const selectedGuestName = useMemo(() => {
    if (!selectedGuestEmail) return "";
    const found = instructorsDropdown.find(
      (i) => i.value === selectedGuestEmail,
    );
    return found?.name || "";
  }, [selectedGuestEmail, instructorsDropdown]);

  // Hold duration in days
  const holdDays = useMemo(() => {
    if (!holdFromStr || !holdToStr || holdToStr < holdFromStr) return null;
    const from = new Date(holdFromStr);
    const to = new Date(holdToStr);
    return (
      Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1
    );
  }, [holdFromStr, holdToStr]);

  const handleClose = () => {
    setShowNoOptions(false);
    setShowLeaveConfirm(false);
    setLeaveIsIssue(false);
    setLeaveIssueDescription("");
    setShowGuestLectureForm(false);
    setSelectedGuestEmail(null);
    setGuestNumSessions(0);
    setGuestStudents([]);
    setShowHoldForm(false);
    setHoldFromStr("");
    setHoldToStr("");
    setHoldComment("");
    onClose();
  };

  const handleYes = () => {
    setShowNoOptions(false);
    onClassHappened();
  };

  const handleNo = () => {
    if (isFirstJourney) {
      handleClose();
      return;
    }
    setShowNoOptions(true);
  };

  const handleLeaveClick = () => setShowLeaveConfirm(true);

  const handleLeaveConfirm = () => {
    setShowNoOptions(false);
    setShowLeaveConfirm(false);
    onQuickSave(
      "Leave",
      leaveIsIssue
        ? { is_issue: true, issue_description: leaveIssueDescription }
        : undefined,
    );
    setLeaveIsIssue(false);
    setLeaveIssueDescription("");
  };

  const handleQuickSave = (
    status: "Holiday" | "Not Started" | "Not Schedule",
  ) => {
    setShowNoOptions(false);
    onQuickSave(status);
  };

  const handleGuestLectureClick = () => {
    // Initialize guest lecture state with current values (auto-increment by 1)
    setGuestNumSessions(currentNumSessions + 1);
    setGuestStudents(
      currentStudents.map((s) => ({
        ...s,
        studentClasses: String(parseInt(s.studentClasses || "0", 10) + 1),
      })),
    );
    setShowGuestLectureForm(true);
  };

  const handleGuestStudentClassChange = (index: number, value: string) => {
    setGuestStudents((prev) =>
      prev.map((s, i) => (i === index ? { ...s, studentClasses: value } : s)),
    );
  };

  const handleGuestLectureConfirm = () => {
    if (!selectedGuestEmail || !onGuestLecture) return;
    setShowNoOptions(false);
    setShowGuestLectureForm(false);
    onGuestLecture(
      selectedGuestEmail,
      selectedGuestName,
      guestNumSessions,
      guestStudents,
    );
    setSelectedGuestEmail(null);
    setGuestNumSessions(0);
    setGuestStudents([]);
  };

  const handleHoldClick = () => {
    setHoldFromStr(toInputValue(selectedDate || new Date()));
    setHoldToStr("");
    setHoldComment("");
    setShowHoldForm(true);
  };

  const handleHoldConfirm = () => {
    if (!holdFromStr || !holdToStr || !holdComment.trim() || !onHoldBatch) return;
    setShowNoOptions(false);
    setShowHoldForm(false);
    onHoldBatch(holdFromStr, holdToStr, holdComment.trim());
    setHoldFromStr("");
    setHoldToStr("");
    setHoldComment("");
  };

  // Shared native date input style
  const dateInputStyle = (disabled = false): React.CSSProperties => ({
    width: "100%",
    padding: "8px 12px",
    fontSize: "0.85rem",
    border: "1.5px solid #f59e0b",
    borderRadius: "6px",
    outline: "none",
    backgroundColor: disabled ? "#f9f9f9" : "#fff",
    color: disabled ? "#aaa" : "#221E45",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    boxSizing: "border-box",
  });

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="md"
      centered
      title={
        <div className="flex items-center gap-3">
          <Text className="text-lg font-bold text-[#221E45]">
            📋 Class Status
          </Text>
          {selectedDate && (
            <Badge
              size="sm"
              className="bg-[#FC7A69]/15 text-[#FC7A69] font-semibold"
            >
              {formatDate(selectedDate)}
            </Badge>
          )}
        </div>
      }
      styles={{
        header: { borderBottom: "2px solid #FC7A69", paddingBottom: "1rem" },
      }}
    >
      <Stack gap="lg" className="py-4">
        {/* ── End Hold screen — shown when clicking on a hold date ── */}
        {isHoldDate && onEndHoldFromDate ? (
          <>
            <Text className="text-center text-base font-medium text-[#221E45]">
              ⏸ This Date is on Hold
            </Text>

            <Paper
              p="md"
              withBorder
              radius="md"
              className="bg-rose-50/50 border-rose-200"
            >
              <Stack gap="sm" align="center">
                <Text size="sm" className="text-[#221E45]/80 text-center">
                  Hold period: <strong>{holdFromDate}</strong> →{" "}
                  <strong>{holdToDate}</strong>
                </Text>
                <Text size="xs" c="dimmed" className="text-center">
                  End the hold from the selected date onward. All hold entries
                  from this date will be removed so you can resume marking
                  journeys.
                </Text>
              </Stack>
            </Paper>

            <Group grow gap="md">
              <Button
                variant="subtle"
                onClick={handleClose}
                className="text-[#221E45]/60 hover:bg-[#221E45]/5"
              >
                ← Close
              </Button>
              <Button
                onClick={() => {
                  if (selectedDate) {
                    onEndHoldFromDate(toInputValue(selectedDate));
                    handleClose();
                  }
                }}
                loading={isSubmitting}
                className="bg-green-600 hover:bg-green-700 text-white"
                leftSection={<IconCalendarMinus size={18} />}
                radius="md"
              >
                End Hold from {selectedDate ? formatDate(selectedDate) : ""}
              </Button>
            </Group>
          </>
        ) : !showNoOptions &&
          !showLeaveConfirm &&
          !showGuestLectureForm &&
          !showHoldForm ? (
          <>
            <Text className="text-center text-base font-medium text-[#221E45]">
              Did the class happen?
            </Text>

            {isFirstJourney && (
              <Text size="xs" className="text-orange-600 text-center">
                This is your first journey entry. Leave/Holiday options are
                disabled.
              </Text>
            )}

            <Group grow gap="md">
              <Button
                size="lg"
                onClick={handleYes}
                className="bg-green-500 hover:bg-green-600 text-white"
                leftSection={<IconCheck size={20} />}
                radius="md"
              >
                Yes
              </Button>
              <Button
                size="lg"
                onClick={handleNo}
                className="bg-red-400 hover:bg-red-500 text-white"
                leftSection={<IconX size={20} />}
                radius="md"
              >
                No
              </Button>
            </Group>

            {currentBatchStatus === "Hold" && onUnholdBatch && (
              <Button
                size="md"
                onClick={() => {
                  onUnholdBatch();
                  handleClose();
                }}
                loading={isSubmitting}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                leftSection={<IconPlayerPlay size={20} />}
                radius="md"
                fullWidth
              >
                ▶ Unhold Batch (Resume)
              </Button>
            )}
          </>
        ) : showLeaveConfirm ? (
          /* ── Leave Confirmation ── */
          <>
            <Text className="text-center text-base font-medium text-[#221E45]">
              Marking <strong>Leave</strong> for{" "}
              {selectedDate ? formatDate(selectedDate) : ""}
            </Text>

            <Paper
              p="md"
              withBorder
              radius="md"
              className={
                leaveIsIssue
                  ? "bg-red-50/50 border-red-300"
                  : "bg-gray-50/50 border-gray-200"
              }
            >
              <Checkbox
                label={
                  <Text
                    size="sm"
                    fw={600}
                    className={leaveIsIssue ? "text-red-700" : "text-[#221E45]"}
                  >
                    ⚠️ Mark as Issue
                  </Text>
                }
                checked={leaveIsIssue}
                onChange={(e) => setLeaveIsIssue(e.currentTarget.checked)}
                color="red"
                styles={{ input: { cursor: "pointer" } }}
              />
              {leaveIsIssue && (
                <Textarea
                  mt="sm"
                  label={
                    <Text className="text-xs font-semibold text-red-700">
                      Issue Description
                    </Text>
                  }
                  placeholder="Describe the issue (e.g. Unplanned leave, no prior notice...)"
                  value={leaveIssueDescription}
                  onChange={(e) =>
                    setLeaveIssueDescription(e.currentTarget.value)
                  }
                  minRows={2}
                  styles={{
                    input: { fontSize: "0.75rem", borderColor: "#ef4444" },
                  }}
                />
              )}
            </Paper>

            <Group grow gap="md">
              <Button
                variant="subtle"
                onClick={() => {
                  setShowLeaveConfirm(false);
                  setLeaveIsIssue(false);
                  setLeaveIssueDescription("");
                }}
                className="text-[#221E45]/60 hover:bg-[#221E45]/5"
              >
                ← Go Back
              </Button>
              <Button
                onClick={handleLeaveConfirm}
                loading={isSubmitting}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                leftSection={<IconBeach size={18} />}
                radius="md"
              >
                Confirm Leave
              </Button>
            </Group>
          </>
        ) : showGuestLectureForm ? (
          /* ── Guest Lecture Form ── */
          <>
            <Text className="text-center text-base font-medium text-[#221E45]">
              Assign Guest Lecturer
            </Text>

            <Paper
              p="md"
              withBorder
              radius="md"
              className="bg-teal-50/50 border-teal-200"
            >
              <Stack gap="sm">
                <Select
                  label={
                    <Text className="text-xs font-semibold text-[#221E45]">
                      Guest Trainer Email
                    </Text>
                  }
                  placeholder="Search and select trainer email..."
                  data={instructorsDropdown}
                  value={selectedGuestEmail}
                  onChange={setSelectedGuestEmail}
                  searchable
                  required
                  styles={{
                    input: { fontSize: "0.8rem", borderColor: "#14b8a6" },
                  }}
                />
                {selectedGuestEmail && (
                  <TextInput
                    label={
                      <Text className="text-xs font-semibold text-[#221E45]">
                        Guest Trainer Name
                      </Text>
                    }
                    value={selectedGuestName}
                    readOnly
                    styles={{
                      input: {
                        fontSize: "0.8rem",
                        backgroundColor: "#f0fdfa",
                        borderColor: "#14b8a6",
                        color: "#0f766e",
                        fontWeight: 600,
                      },
                    }}
                  />
                )}
              </Stack>
            </Paper>

            {/* ── Number of Sessions ── */}
            <Paper
              p="md"
              withBorder
              radius="md"
              className="bg-blue-50/50 border-blue-200"
            >
              <NumberInput
                label={
                  <Text className="text-xs font-semibold text-[#221E45]">
                    📚 Number of Sessions
                  </Text>
                }
                value={guestNumSessions}
                onChange={(val) => setGuestNumSessions(Number(val) || 0)}
                min={0}
                styles={{
                  input: {
                    fontSize: "0.85rem",
                    borderColor: "#3b82f6",
                    fontWeight: 600,
                  },
                }}
              />
            </Paper>

            {/* ── Students & Classes ── */}
            {guestStudents.length > 0 && (
              <Paper
                p="md"
                withBorder
                radius="md"
                className="bg-purple-50/50 border-purple-200"
              >
                <Group gap="xs" mb="sm">
                  <IconUserCheck size={18} className="text-purple-600" />
                  <Text className="text-xs font-semibold text-[#221E45]">
                    Students ({guestStudents.length})
                  </Text>
                </Group>

                <ScrollArea.Autosize mah={220}>
                  <Stack gap="xs">
                    {guestStudents.map((student, idx) => (
                      <Paper
                        key={student.email}
                        p="xs"
                        withBorder
                        radius="sm"
                        className="bg-white border-purple-100"
                      >
                        <Group justify="space-between" wrap="nowrap" gap="sm">
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text
                              size="sm"
                              fw={600}
                              className="text-[#221E45]"
                              truncate
                            >
                              {student.name}
                            </Text>
                            <Text size="xs" c="dimmed" truncate>
                              {student.email}
                            </Text>
                          </div>
                          <NumberInput
                            value={parseInt(student.studentClasses || "0", 10)}
                            onChange={(val) =>
                              handleGuestStudentClassChange(
                                idx,
                                String(val || 0),
                              )
                            }
                            min={0}
                            label={
                              <Text
                                size="xs"
                                fw={600}
                                className="text-purple-700"
                              >
                                Classes
                              </Text>
                            }
                            styles={{
                              root: { width: 90 },
                              input: {
                                fontSize: "0.8rem",
                                borderColor: "#a855f7",
                                fontWeight: 600,
                                textAlign: "center",
                              },
                            }}
                          />
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </ScrollArea.Autosize>
              </Paper>
            )}

            <Group grow gap="md">
              <Button
                variant="subtle"
                onClick={() => {
                  setShowGuestLectureForm(false);
                  setSelectedGuestEmail(null);
                  setGuestNumSessions(0);
                  setGuestStudents([]);
                }}
                className="text-[#221E45]/60 hover:bg-[#221E45]/5"
              >
                ← Go Back
              </Button>
              <Button
                onClick={handleGuestLectureConfirm}
                loading={isSubmitting}
                className="bg-teal-500 hover:bg-teal-600 text-white"
                leftSection={<IconSchool size={18} />}
                radius="md"
                disabled={!selectedGuestEmail}
              >
                Confirm Guest Lecture
              </Button>
            </Group>
          </>
        ) : showHoldForm ? (
          /* ── Hold Form — uses native <input type="date"> to avoid popover overflow ── */
          <>
            <Text className="text-center text-base font-medium text-[#221E45]">
              ⏸ Put Batch on Hold
            </Text>

            <Paper
              p="md"
              withBorder
              radius="md"
              className="bg-amber-50/50 border-amber-200"
            >
              <Stack gap="md">
                {/* Hold From */}
                <div>
                  <Text size="xs" fw={600} mb={4} className="text-[#221E45]">
                    Hold From
                  </Text>
                  <input
                    type="date"
                    value={holdFromStr}
                    max={today}
                    onChange={(e) => {
                      setHoldFromStr(e.target.value);
                      if (holdToStr && holdToStr < e.target.value)
                        setHoldToStr("");
                    }}
                    style={dateInputStyle()}
                  />
                </div>

                {/* Hold To */}
                <div>
                  <Text size="xs" fw={600} mb={4} className="text-[#221E45]">
                    Hold To
                  </Text>
                  <input
                    type="date"
                    value={holdToStr}
                    min={holdFromStr || undefined}
                    disabled={!holdFromStr}
                    onChange={(e) => setHoldToStr(e.target.value)}
                    style={dateInputStyle(!holdFromStr)}
                  />
                  {!holdFromStr && (
                    <Text size="xs" c="dimmed" mt={4}>
                      Please select "Hold From" first
                    </Text>
                  )}
                </div>

                {/* Hold reason (mandatory) */}
                <Textarea
                  label={
                    <Text size="xs" fw={600} className="text-[#221E45]">
                      Change Summary
                    </Text>
                  }
                  description="Mandatory. Shown as a tooltip on the batch status."
                  placeholder="Why is this batch being put on hold?"
                  value={holdComment}
                  onChange={(e) => setHoldComment(e.currentTarget.value)}
                  minRows={2}
                  withAsterisk
                  styles={{ input: { fontSize: "0.8rem", borderColor: "#f59e0b" } }}
                />

                {/* Duration badge */}
                {holdDays !== null && (
                  <Text
                    size="xs"
                    fw={600}
                    className="text-amber-700 text-center"
                  >
                    📅 Hold period: {holdDays} day{holdDays !== 1 ? "s" : ""}
                  </Text>
                )}
              </Stack>
            </Paper>

            <Group grow gap="md">
              <Button
                variant="subtle"
                onClick={() => {
                  setShowHoldForm(false);
                  setHoldFromStr("");
                  setHoldToStr("");
                  setHoldComment("");
                }}
                className="text-[#221E45]/60 hover:bg-[#221E45]/5"
              >
                ← Go Back
              </Button>
              <Button
                onClick={handleHoldConfirm}
                loading={isSubmitting}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                leftSection={<IconPlayerPause size={18} />}
                radius="md"
                disabled={!holdFromStr || !holdToStr || !holdComment.trim()}
              >
                Confirm Hold
              </Button>
            </Group>
          </>
        ) : (
          /* ── Why didn't class happen? ── */
          <>
            <Text className="text-center text-base font-medium text-[#221E45]">
              Why didn't the class happen?
            </Text>

            <SimpleGrid cols={2} spacing="md">
              <Paper
                p="md"
                withBorder
                radius="md"
                className="cursor-pointer text-center hover:bg-orange-50 hover:border-orange-400 transition-all duration-200 border-2"
                onClick={handleLeaveClick}
                style={{ pointerEvents: isSubmitting ? "none" : "auto" }}
              >
                <Stack align="center" gap="xs">
                  <IconBeach size={32} className="text-orange-500" />
                  <Text className="font-bold text-[#221E45]">Leave</Text>
                  <Text size="xs" c="dimmed">
                    Trainer was absent
                  </Text>
                </Stack>
              </Paper>

              <Paper
                p="md"
                withBorder
                radius="md"
                className="cursor-pointer text-center hover:bg-blue-50 hover:border-blue-400 transition-all duration-200 border-2"
                onClick={() => handleQuickSave("Holiday")}
                style={{ pointerEvents: isSubmitting ? "none" : "auto" }}
              >
                <Stack align="center" gap="xs">
                  <IconCalendarOff size={32} className="text-blue-500" />
                  <Text className="font-bold text-[#221E45]">Holiday</Text>
                  <Text size="xs" c="dimmed">
                    Scheduled holiday
                  </Text>
                </Stack>
              </Paper>

              <Paper
                p="md"
                withBorder
                radius="md"
                className="cursor-pointer text-center hover:bg-purple-50 hover:border-purple-400 transition-all duration-200 border-2"
                onClick={() => handleQuickSave("Not Started")}
                style={{ pointerEvents: isSubmitting ? "none" : "auto" }}
              >
                <Stack align="center" gap="xs">
                  <IconTool size={32} className="text-purple-500" />
                  <Text className="font-bold text-[#221E45]">Not Started</Text>
                  <Text size="xs" c="dimmed">
                    Tool unavailable
                  </Text>
                </Stack>
              </Paper>

              <Paper
                p="md"
                withBorder
                radius="md"
                className="cursor-pointer text-center hover:bg-indigo-50 hover:border-indigo-400 transition-all duration-200 border-2"
                onClick={() => handleQuickSave("Not Schedule")}
                style={{ pointerEvents: isSubmitting ? "none" : "auto" }}
              >
                <Stack align="center" gap="xs">
                  <IconCalendarOff size={32} className="text-indigo-500" />
                  <Text className="font-bold text-[#221E45]">Not Schedule</Text>
                  <Text size="xs" c="dimmed">
                    Class not scheduled
                  </Text>
                </Stack>
              </Paper>

              <Paper
                p="md"
                withBorder
                radius="md"
                className="cursor-pointer text-center hover:bg-teal-50 hover:border-teal-400 transition-all duration-200 border-2"
                onClick={handleGuestLectureClick}
                style={{ pointerEvents: isSubmitting ? "none" : "auto" }}
              >
                <Stack align="center" gap="xs">
                  <IconSchool size={32} className="text-teal-500" />
                  <Text className="font-bold text-[#221E45]">
                    Guest Lecture
                  </Text>
                  <Text size="xs" c="dimmed">
                    Assign guest trainer
                  </Text>
                </Stack>
              </Paper>

              <Paper
                p="md"
                withBorder
                radius="md"
                className="cursor-pointer text-center hover:bg-amber-50 hover:border-amber-400 transition-all duration-200 border-2"
                onClick={handleHoldClick}
                style={{ pointerEvents: isSubmitting ? "none" : "auto" }}
              >
                <Stack align="center" gap="xs">
                  <IconPlayerPause size={32} className="text-amber-500" />
                  <Text className="font-bold text-[#221E45]">Hold</Text>
                  <Text size="xs" c="dimmed">
                    Pause batch (date range)
                  </Text>
                </Stack>
              </Paper>
            </SimpleGrid>

            <Button
              variant="subtle"
              size="sm"
              onClick={() => setShowNoOptions(false)}
              className="text-[#221E45]/60 hover:bg-[#221E45]/5"
            >
              ← Go Back
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default ClassStatusDialog;
